import { io, type Socket } from 'socket.io-client';
import type { ClientToServerEvents, ServerToClientEvents } from '@spawnwire/shared';
import { commandsChanged, upsertInteraction } from '../features/chat/slashCommands';
import { minecraft } from '../features/minecraft/store';
import { standing } from '../features/standing/store';
import { support } from '../features/support/store';
import { actions, useStore } from '../store/store';
import { ui } from '../store/ui';
import { maybeNotify, playChime } from './notifications';
import { getCurrentChannel, navigate } from './nav';
import { native } from './native';
import { API_ORIGIN, getAuthToken, isNative } from './platform';
import { resyncLoadedChannels, refreshMembersIfLoaded } from './data';
import { sendViewing, startViewingTracking } from './viewing';

export type GatewaySocket = Socket<ServerToClientEvents, ClientToServerEvents>;

let socket: GatewaySocket | null = null;
const readyListeners = new Set<() => void>();

export function getSocket(): GatewaySocket | null {
  return socket;
}

/** Called after every `ready` (first connect and each reconnect). */
export function onReady(fn: () => void): () => void {
  readyListeners.add(fn);
  return () => readyListeners.delete(fn);
}

type Handlers = { [E in keyof ServerToClientEvents]?: (...args: Parameters<ServerToClientEvents[E]>) => void };

const handlers: Handlers = {
  'message:create': (m) => {
    actions.upsertMessage(m, { fromSocket: true });
    maybeNotify(m);
  },
  'message:update': (m) => actions.updateMessage(m),
  'message:delete': ({ id, channelId }) => actions.removeMessage(channelId, id),
  'reaction:update': (p) => actions.applyReactions(p.channelId, p.messageId, p.reactions, p.userId, p.emoji, p.added),
  'typing:start': ({ channelId, userId }) => {
    if (userId !== useStore.getState().me?.id) actions.setTyping(channelId, userId, Date.now());
  },
  'presence:update': ({ userId, status }) => actions.setPresence(userId, status),
  'channel:create': (c) => actions.upsertChannel(c),
  'channel:update': (c) => actions.upsertChannel(c),
  'channel:delete': ({ id }) => {
    const wasOpen = getCurrentChannel() === id;
    const channel = useStore.getState().channels[id];
    actions.removeChannel(id);
    if (wasOpen) navigate(channel?.spaceId ? `/app/${channel.spaceId}` : '/app/@me');
  },
  'space:create': ({ space, member }) => {
    actions.upsertSpace(space, member);
    refreshMembersIfLoaded(space.id);
  },
  'space:update': (summary) => actions.updateSpaceSummary(summary),
  'space:delete': ({ id, reason }) => {
    const space = useStore.getState().spaces[id];
    actions.removeSpace(id);
    if (location.pathname.startsWith(`/app/${id}`)) navigate('/app/@me');
    if (reason === 'kicked') ui.toast(`You were removed from ${space?.name ?? 'a space'}.`, 'error');
    if (reason === 'banned') ui.toast(`You were banned from ${space?.name ?? 'a space'}.`, 'error');
    if (reason === 'deleted') ui.toast(`${space?.name ?? 'A space'} was deleted.`);
  },
  'member:add': (m) => actions.upsertMember(m),
  'member:update': (m) => actions.upsertMember(m),
  'member:remove': ({ spaceId, userId }) => actions.removeMember(spaceId, userId),
  'role:create': (r) => actions.upsertRole(r),
  'role:update': (r) => actions.upsertRole(r),
  'role:delete': ({ spaceId, id }) => actions.removeRole(spaceId, id),
  'read_state:update': (s) => {
    actions.setReadState(s);
    // Read here or on another device: the phone's notification for it goes.
    if (s.mentionCount === 0) native?.conversationRead(s.channelId);
  },
  'relationship:update': (r) => {
    const prev = useStore.getState().relationships[r.userId];
    actions.upsertRelationship(r);
    if (r.type === 'incoming' && prev?.type !== 'incoming') {
      ui.toast(`${r.user.displayName} sent you a friend request.`);
      if (useStore.getState().me?.settings.notificationSounds) playChime('message');
    }
  },
  'relationship:remove': ({ userId }) => actions.removeRelationship(userId),
  'user:update': (u) => actions.upsertUser(u),
  'self:update': (u) => actions.setMe(u),
  'notification_setting:update': (n) => actions.setNotificationSetting(n),
  'voice:state': ({ channelId, userId, state }) => actions.setVoiceState(channelId, userId, state),
  'call:ring': ({ channelId, callerId }) => {
    actions.setRinging({ channelId, callerId });
  },
  'call:ring_stop': ({ channelId }) => {
    if (useStore.getState().ringing?.channelId === channelId) actions.setRinging(null);
  },
  'mc:update': (server) => minecraft.upsert(server),
  'mc:delete': ({ serverId }) => minecraft.remove(serverId),
  'mc:console': ({ serverId, lines }) => minecraft.appendConsole(serverId, lines),
  'session:revoked': () => {
    void native?.signedOut();
    actions.reset();
    navigate('/login');
  },
  'standing:update': (s) => standing.set(s),
  'announcement:publish': (m) => maybeNotify(m, { announcement: true }),
  'support:update': (t) => support.upsert(t),
  'interaction:update': (state) => upsertInteraction(state),
  'commands:update': ({ spaceId }) => commandsChanged(spaceId),
  'account:banned': (ban) => {
    disconnectGateway();
    standing.banned(ban);
  },
};

function dispatch(event: string, args: unknown[]): void {
  const handler = (handlers as Record<string, ((...a: unknown[]) => void) | undefined>)[event];
  handler?.(...args);
}

export function connectGateway(): GatewaySocket {
  if (socket) return socket;
  const options = { path: '/socket.io', transports: ['websocket', 'polling'], reconnectionDelayMax: 8000 };
  // The app connects across origins with its token (read on every reconnect); the website uses its cookie.
  const s: GatewaySocket = isNative
    ? io(API_ORIGIN, { ...options, withCredentials: false, auth: (cb) => cb({ token: getAuthToken() ?? '' }) })
    : io({ ...options, withCredentials: true });
  socket = s;
  let buffering = true;
  let buffer: [string, unknown[]][] = [];
  let everReady = false;

  s.on('connect', () => {
    buffering = true;
    buffer = [];
    actions.setConnection(everReady ? 'reconnecting' : 'connecting');
  });

  s.on('ready', (payload) => {
    actions.applyReady(payload);
    // Replay events that arrived between joining rooms and the snapshot.
    for (const [event, args] of buffer) dispatch(event, args);
    buffer = [];
    buffering = false;
    actions.setConnection('ready');
    sendViewing();
    native?.syncNotifications();
    if (everReady) void resyncLoadedChannels();
    everReady = true;
    for (const fn of readyListeners) fn();
  });

  s.onAny((event: string, ...args: unknown[]) => {
    if (event === 'ready') return;
    if (buffering) buffer.push([event, args]);
    else dispatch(event, args);
  });

  s.on('disconnect', (reason) => {
    actions.setConnection('reconnecting');
    // The server closed the connection deliberately (e.g. session revoked): reconnect manually.
    // A socket we have since dropped (e.g. after a ban) stays closed.
    if (reason === 'io server disconnect') setTimeout(() => socket === s && s.connect(), 1500);
  });

  s.on('connect_error', (err) => {
    actions.setConnection('reconnecting');
    if (err.message === 'unauthorized') {
      s.disconnect();
      socket = null;
      void native?.signedOut();
      actions.reset();
      navigate('/login');
    }
    if (err.message === 'account_banned') {
      s.disconnect();
      socket = null;
      // Fetching the standing brings back the ban, which switches the app to the ban screen.
      void standing.load().catch(() => {});
    }
  });

  window.addEventListener('online', () => {
    if (!s.connected) s.connect();
  });
  return s;
}

export function disconnectGateway(): void {
  socket?.disconnect();
  socket = null;
}

// ---- Typing, idle and housekeeping --------------------------------------------

const lastTyping = new Map<string, number>();

export function sendTyping(channelId: string): void {
  const now = Date.now();
  if (now - (lastTyping.get(channelId) ?? 0) < 6000) return;
  lastTyping.set(channelId, now);
  socket?.emit('typing:start', { channelId });
}

export function clearTypingThrottle(channelId: string): void {
  lastTyping.delete(channelId);
}

const IDLE_AFTER_MS = 10 * 60 * 1000;
let idle = false;
let idleTimer: ReturnType<typeof setTimeout> | null = null;

function markActive(): void {
  if (idle) {
    idle = false;
    socket?.emit('presence:idle', { idle: false });
  }
  if (idleTimer) clearTimeout(idleTimer);
  idleTimer = setTimeout(() => {
    idle = true;
    socket?.emit('presence:idle', { idle: true });
  }, IDLE_AFTER_MS);
}

let housekeepingStarted = false;
export function startHousekeeping(): void {
  if (housekeepingStarted) return;
  housekeepingStarted = true;
  startViewingTracking();
  for (const ev of ['mousemove', 'keydown', 'pointerdown', 'focus', 'touchstart']) window.addEventListener(ev, markActive, { passive: true });
  markActive();
  setInterval(() => actions.pruneTyping(Date.now()), 2000);
}
